import React, { useEffect } from 'react';

import './LogoOptions.css';

const palette = {
  cyan: '#00d5ff',
  pink: '#ff2d95',
  green: '#32ff6a',
  yellow: '#ffd60a',
  purple: '#8b5cf6',
  white: '#ffffff',
  black: '#050505',
};

const options = [
  {
    id: 'signal',
    name: 'Signal Stack',
    tone: 'Bold, app-icon friendly, very readable at small sizes.',
  },
  {
    id: 'orbit',
    name: 'Connection Orbit',
    tone: 'Feels like a living map without becoming too literal.',
  },
  {
    id: 'path',
    name: 'Story Path',
    tone: 'Friendly and human, with the map line built into the mark.',
  },
  {
    id: 'prism',
    name: 'Prism Wordmark',
    tone: 'Premium, clean, strongest for website header branding.',
  },
  {
    id: 'knot',
    name: 'Social Knot',
    tone: 'Compact, memorable, works well as a favicon.',
  },
  {
    id: 'spark',
    name: 'Meet Cute Spark',
    tone: 'Warm, playful, good if the product should feel more story-led.',
  },
];

function LogoSignal() {
  return (
    <svg viewBox="0 0 220 220" role="img" aria-label="Signal Stack hymm logo option">
      <rect x="20" y="20" width="180" height="180" rx="42" fill={palette.black} />
      <path d="M58 150V74" stroke={palette.cyan} strokeWidth="18" strokeLinecap="round" />
      <path d="M95 150V98" stroke={palette.pink} strokeWidth="18" strokeLinecap="round" />
      <path d="M132 150V62" stroke={palette.green} strokeWidth="18" strokeLinecap="round" />
      <path d="M169 150V116" stroke={palette.yellow} strokeWidth="18" strokeLinecap="round" />
      <text x="110" y="184" textAnchor="middle" fill={palette.white} fontSize="32" fontWeight="900">hymm</text>
    </svg>
  );
}

function LogoOrbit() {
  return (
    <svg viewBox="0 0 220 220" role="img" aria-label="Connection Orbit hymm logo option">
      <rect x="18" y="18" width="184" height="184" rx="92" fill={palette.black} />
      <circle cx="110" cy="110" r="56" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="6" />
      <path d="M62 132C84 82 132 74 162 103C178 119 174 148 148 154C118 162 91 141 77 111" fill="none" stroke={palette.cyan} strokeWidth="10" strokeLinecap="round" />
      <circle cx="62" cy="132" r="14" fill={palette.pink} />
      <circle cx="162" cy="103" r="14" fill={palette.yellow} />
      <circle cx="148" cy="154" r="12" fill={palette.green} />
      <text x="110" y="119" textAnchor="middle" fill={palette.white} fontSize="38" fontWeight="950">hymm</text>
    </svg>
  );
}

function LogoPath() {
  return (
    <svg viewBox="0 0 300 180" role="img" aria-label="Story Path hymm logo option">
      <rect x="16" y="24" width="268" height="132" rx="34" fill={palette.black} />
      <path d="M54 108C82 58 117 137 151 88C181 44 208 76 246 52" fill="none" stroke={palette.cyan} strokeWidth="9" strokeLinecap="round" />
      <circle cx="54" cy="108" r="13" fill={palette.pink} />
      <circle cx="151" cy="88" r="13" fill={palette.green} />
      <circle cx="246" cy="52" r="13" fill={palette.yellow} />
      <text x="150" y="126" textAnchor="middle" fill={palette.white} fontSize="46" fontWeight="950">hymm</text>
    </svg>
  );
}

function LogoPrism() {
  return (
    <svg viewBox="0 0 340 180" role="img" aria-label="Prism Wordmark hymm logo option">
      <rect x="14" y="28" width="312" height="124" rx="28" fill={palette.black} />
      <text x="170" y="106" textAnchor="middle" fill={palette.white} fontSize="58" fontWeight="950">hymm</text>
      <rect x="50" y="124" width="55" height="8" rx="4" fill={palette.cyan} />
      <rect x="112" y="124" width="55" height="8" rx="4" fill={palette.pink} />
      <rect x="174" y="124" width="55" height="8" rx="4" fill={palette.green} />
      <rect x="236" y="124" width="55" height="8" rx="4" fill={palette.yellow} />
    </svg>
  );
}

function LogoKnot() {
  return (
    <svg viewBox="0 0 220 220" role="img" aria-label="Social Knot hymm logo option">
      <rect x="20" y="20" width="180" height="180" rx="48" fill={palette.black} />
      <path d="M70 75C104 38 151 65 135 105C125 129 92 131 79 108C64 82 87 56 118 70C151 86 158 139 114 154C81 166 59 141 68 113" fill="none" stroke={palette.cyan} strokeWidth="11" strokeLinecap="round" />
      <path d="M91 147C123 172 165 150 155 111" fill="none" stroke={palette.pink} strokeWidth="11" strokeLinecap="round" />
      <circle cx="70" cy="75" r="11" fill={palette.yellow} />
      <circle cx="114" cy="154" r="11" fill={palette.green} />
      <text x="110" y="118" textAnchor="middle" fill={palette.white} fontSize="34" fontWeight="950">hymm</text>
    </svg>
  );
}

function LogoSpark() {
  return (
    <svg viewBox="0 0 220 220" role="img" aria-label="Meet Cute Spark hymm logo option">
      <rect x="20" y="20" width="180" height="180" rx="90" fill={palette.black} />
      <path d="M110 49L122 88L163 88L130 112L143 152L110 127L77 152L90 112L57 88L98 88Z" fill="none" stroke={palette.yellow} strokeWidth="9" strokeLinejoin="round" />
      <path d="M75 136C98 114 124 114 147 136" fill="none" stroke={palette.pink} strokeWidth="9" strokeLinecap="round" />
      <text x="110" y="117" textAnchor="middle" fill={palette.white} fontSize="34" fontWeight="950">hymm</text>
      <circle cx="62" cy="62" r="8" fill={palette.cyan} />
      <circle cx="158" cy="160" r="8" fill={palette.green} />
    </svg>
  );
}

function LogoMark({ id }) {
  if (id === 'signal') return <LogoSignal />;
  if (id === 'orbit') return <LogoOrbit />;
  if (id === 'path') return <LogoPath />;
  if (id === 'prism') return <LogoPrism />;
  if (id === 'knot') return <LogoKnot />;
  return <LogoSpark />;
}

export default function LogoOptions() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Logo options | How you met me';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className="logo-options-page">
      <section className="logo-options-hero">
        <div>
          <span>How You Met Me</span>
          <h1>Logo directions</h1>
          <p>Six clean, scalable directions using the relationship-category color system. Pick the one with the right personality and I can turn it into the real app icon, favicon, and header mark.</p>
        </div>
      </section>

      <section className="logo-options-grid" aria-label="Logo options">
        {options.map((option, index) => (
          <article className="logo-option-card" key={option.id}>
            <div className="logo-option-number">{String(index + 1).padStart(2, '0')}</div>
            <div className="logo-option-stage">
              <LogoMark id={option.id} />
            </div>
            <div className="logo-option-copy">
              <h2>{option.name}</h2>
              <p>{option.tone}</p>
            </div>
            <div className="logo-option-wordmark">
              <strong>How you met me</strong>
              <span>hymm</span>
            </div>
          </article>
        ))}
      </section>
    </main>
  );
}
